import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Radio, Eye, Clock, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine } from "../components/Reveal";
import { CoverageChecker } from "../components/CoverageChecker";
import { FaqAccordion } from "../components/FaqAccordion";
import { Testimonials } from "../components/Testimonials";
import { CtaBand } from "../components/CtaBand";
import { SERVICES } from "../data/services";
import { CITIES } from "../data/cities";
import { BLOG_POSTS } from "../data/blog";
import { IMAGES, MARQUEE_ITEMS, HOME_FAQS } from "../data/site";

const STATS = [
    { icon: MapPin, value: "46+", label: "Scottish towns & cities covered" },
    { icon: ShieldCheck, value: "17", label: "Specialist security services" },
    { icon: Clock, value: "24/7", label: "Scottish command & response" },
    { icon: Eye, value: "100%", label: "SIA licensed & BS 7858 vetted" },
];

const BADGES = ["SIA Licensed", "BS 7858 Vetted", "£10m Insured", "24/7 Control Room"];

export default function Home() {
    const { scrollY } = useScroll();
    const heroY = useTransform(scrollY, [0, 900], [0, 260]);
    const heroScale = useTransform(scrollY, [0, 900], [1.05, 1.22]);

    return (
        <div data-testid="home-page">
            <Seo
                title="Security Guards in Scotland | Glasgow, Edinburgh & Aberdeen | Scotland Security"
                description="Security guards, mobile patrols and event security for Scottish businesses. Discuss coverage in Glasgow, Edinburgh, Aberdeen and beyond."
            />

            <section className="relative min-h-screen flex items-end overflow-hidden xp-grain" data-testid="home-hero">
                <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }}>
                    <img src={IMAGES.hero} alt="Edinburgh skyline at night — security guard services Scotland" className="w-full h-full object-cover" />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-[#07080B]" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-36 pt-44 w-full">
                    <motion.p
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="font-mono-xp text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#E53E3E] mb-6"
                    >
                        Scotland Security Operations // SIA Licensed
                    </motion.p>
                    <h1 className="font-display uppercase font-extrabold text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.92] text-white">
                        <MaskedLine delay={0.35}>Security Guard</MaskedLine>
                        <MaskedLine delay={0.5}>Services</MaskedLine>
                        <MaskedLine delay={0.65} className="text-[#E53E3E]">Across Scotland</MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="text-gray-300 text-sm sm:text-base leading-relaxed mt-7 max-w-2xl"
                    >
                        Xtreme Protection deploys SIA-licensed security guards in Glasgow, Edinburgh and Aberdeen, mobile patrols
                        in Dundee, event security for Edinburgh's festival season and rapid response teams across 46+ Scottish
                        towns and cities — engineered deterrence with verified proof on every shift.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.05 }}
                        className="flex flex-col sm:flex-row gap-4 mt-9"
                    >
                        <Link
                            to="/locations"
                            data-testid="hero-locations-cta"
                            className="xp-glow flex items-center justify-center gap-3 bg-[#E53E3E] hover:bg-[#c93636] transition-colors duration-300 text-white font-mono-xp text-xs uppercase tracking-widest px-8 py-4"
                        >
                            Explore 46+ Locations <ArrowRight size={15} />
                        </Link>
                        <Link
                            to="/services"
                            data-testid="hero-services-cta"
                            className="flex items-center justify-center gap-3 border border-white/30 hover:border-[#E53E3E] hover:text-[#E53E3E] transition-colors duration-300 text-white font-mono-xp text-xs uppercase tracking-widest px-8 py-4"
                        >
                            View 17 Security Services <ArrowRight size={15} />
                        </Link>
                    </motion.div>
                </div>
            </section>

            <div className="border-y border-white/10 bg-[#0F1117] overflow-hidden py-4" data-testid="keyword-marquee" aria-hidden="true">
                <div className="xp-marquee-track flex whitespace-nowrap w-max">
                    {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
                        <span key={i} className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 px-6">
                            {item} <span className="text-[#E53E3E] pl-6">•</span>
                        </span>
                    ))}
                </div>
            </div>

            <CoverageChecker />

            <section className="border-b border-white/10" data-testid="stats-band">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4">
                    {STATS.map((s, i) => (
                        <Reveal key={s.label} delay={i * 0.08} className={`py-12 px-4 text-center ${i > 0 ? "border-l border-white/10" : ""}`}>
                            <s.icon size={20} className="mx-auto text-[#E53E3E] mb-3" />
                            <p className="font-display font-extrabold uppercase text-4xl sm:text-5xl text-white leading-none" data-testid={`stat-value-${i}`}>{s.value}</p>
                            <p className="font-mono-xp text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-2">{s.label}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="standard-section">
                <div className="grid lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">01 — The Standard</p>
                        <h2 className="font-display uppercase font-extrabold text-4xl sm:text-5xl tracking-wide text-white leading-none">
                            Discipline is<br />the <span className="text-[#E53E3E]">deterrent</span>
                        </h2>
                        <p className="text-gray-400 text-sm sm:text-base leading-relaxed mt-6">
                            Xtreme Protection exists because too much of the Scottish security industry runs on promises instead of
                            proof. We run on proof: SIA-licensed officers vetted to BS 7858, GPS and NFC-verified patrols,
                            time-stamped photographic reports and contract managers who answer for performance every single month.
                        </p>
                        <p className="text-gray-400 text-sm sm:text-base leading-relaxed mt-4">
                            From a single night guard in Aberdeen to a multi-site portfolio across the Central Belt, every contract
                            starts the same way — a free risk assessment, honest recommendations, and a written plan we can be held to.
                        </p>
                        <div className="flex flex-wrap gap-2.5 mt-7">
                            {BADGES.map((b) => (
                                <span key={b} className="font-mono-xp text-[10px] uppercase tracking-[0.2em] text-gray-300 border border-white/15 px-3.5 py-2">
                                    {b}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="relative">
                            <div className="absolute -inset-3 border border-[#E53E3E]/25 pointer-events-none" />
                            <img
                                src={IMAGES.patrol}
                                alt="SIA licensed security guard on night patrol in Scotland"
                                className="w-full aspect-[4/5] object-cover"
                                loading="lazy"
                            />
                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                                <p className="font-mono-xp text-[10px] uppercase tracking-[0.3em] text-[#E53E3E]">Verified Patrol // Live Feed</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <Testimonials />

            <section className="bg-[#0F1117] border-y border-white/10 py-24" data-testid="arsenal-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">02 — The Arsenal</p>
                        <h2 className="font-display uppercase font-extrabold text-4xl sm:text-5xl tracking-wide text-white leading-none max-w-3xl">
                            17 specialist services. <span className="text-[#E53E3E]">One accountable partner.</span>
                        </h2>
                        <p className="text-gray-400 text-sm sm:text-base leading-relaxed mt-5 max-w-2xl">
                            Manned guarding, door supervision, CCTV monitoring, mobile patrols, event security, close protection,
                            K9 units, key holding and more — layered into packages that match your actual risk, not a sales template.
                        </p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-12">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.slug} delay={(i % 3) * 0.06}>
                                <Link
                                    to={`/services/${s.slug}`}
                                    data-testid={`arsenal-service-${s.slug}`}
                                    className="group block bg-[#0F1117] hover:bg-[#161922] transition-colors duration-300 p-7 h-full"
                                >
                                    <div className="flex items-start justify-between">
                                        <span className="font-mono-xp text-[11px] text-[#E53E3E] tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                                        <ArrowRight size={16} className="text-gray-600 group-hover:text-[#E53E3E] group-hover:translate-x-1 transition-all duration-300" />
                                    </div>
                                    <h3 className="font-display uppercase font-bold text-2xl tracking-wide text-white mt-4 group-hover:text-[#E53E3E] transition-colors duration-300">
                                        {s.name}
                                    </h3>
                                    <p className="text-gray-500 text-sm leading-relaxed mt-2.5">{s.short}</p>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="territory-section">
                <Reveal>
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div>
                            <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">03 — The Territory</p>
                            <h2 className="font-display uppercase font-extrabold text-4xl sm:text-5xl tracking-wide text-white leading-none max-w-2xl">
                                46+ towns and cities. <span className="text-[#E53E3E]">One command.</span>
                            </h2>
                        </div>
                        <Link
                            to="/locations"
                            data-testid="territory-directory-link"
                            className="flex items-center gap-2 font-mono-xp text-xs uppercase tracking-widest text-[#E53E3E] hover:text-white transition-colors shrink-0"
                        >
                            Full Scotland Directory <ArrowRight size={14} />
                        </Link>
                    </div>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed mt-5 max-w-2xl">
                        National resources with genuinely local teams. Our Scottish command structure means the officer at your gate
                        knows your streets, your police liaison team and your local risk landscape — backed by a 24/7 control room.
                    </p>
                </Reveal>
                <div className="flex flex-wrap gap-2.5 mt-10" data-testid="territory-regions">
                    {[
                        { slug: "central-belt", name: "Central Belt" },
                        { slug: "north-east", name: "North East & Tayside" },
                        { slug: "highlands-islands", name: "Highlands & Islands" },
                        { slug: "south-scotland", name: "South Scotland" },
                    ].map((r) => (
                        <Link
                            key={r.slug}
                            to={`/regions/${r.slug}`}
                            data-testid={`territory-region-${r.slug}`}
                            className="font-mono-xp text-[10px] uppercase tracking-[0.2em] text-gray-300 border border-white/15 hover:border-[#E53E3E] hover:text-[#E53E3E] transition-colors px-3.5 py-2"
                        >
                            {r.name} →
                        </Link>
                    ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-6">
                    {CITIES.map((c, i) => (
                        <Reveal key={c.slug} delay={(i % 4) * 0.05}>
                            <Link
                                to={`/locations/${c.slug}`}
                                data-testid={`territory-city-${c.slug}`}
                                className="group flex items-center justify-between bg-[#07080B] hover:bg-[#161922] transition-colors duration-300 px-5 py-4"
                            >
                                <span>
                                    <span className="block font-display uppercase font-semibold tracking-wide text-gray-200 group-hover:text-[#E53E3E] transition-colors">
                                        Security {c.name}
                                    </span>
                                    <span className="block font-mono-xp text-[10px] uppercase tracking-widest text-gray-600 mt-0.5">{c.region}</span>
                                </span>
                                <ArrowRight size={13} className="text-gray-700 group-hover:text-[#E53E3E] group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-[#0F1117] border-t border-white/10 py-24" data-testid="briefing-section">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">04 — The Briefing</p>
                        <h2 className="font-display uppercase font-extrabold text-4xl sm:text-5xl tracking-wide text-white leading-none">
                            Security questions, <span className="text-[#E53E3E]">answered</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-10">
                        <FaqAccordion faqs={HOME_FAQS} testidPrefix="home-faq" />
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="intel-section">
                <Reveal>
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div>
                            <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">05 — The Intel</p>
                            <h2 className="font-display uppercase font-extrabold text-4xl sm:text-5xl tracking-wide text-white leading-none">
                                Security guides for <span className="text-[#E53E3E]">Scottish business</span>
                            </h2>
                        </div>
                        <Link
                            to="/blog"
                            data-testid="intel-directory-link"
                            className="flex items-center gap-2 font-mono-xp text-xs uppercase tracking-widest text-[#E53E3E] hover:text-white transition-colors shrink-0"
                        >
                            All Intel <ArrowRight size={14} />
                        </Link>
                    </div>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-12">
                    {BLOG_POSTS.slice(0, 3).map((p, i) => (
                        <Reveal key={p.slug} delay={(i % 3) * 0.06}>
                            <Link
                                to={`/blog/${p.slug}`}
                                data-testid={`intel-card-${p.slug}`}
                                className="group block bg-[#07080B] hover:bg-[#161922] transition-colors duration-300 p-7 h-full"
                            >
                                <span className="font-mono-xp text-[10px] uppercase tracking-[0.2em] text-[#E53E3E]">{p.category}</span>
                                <h3 className="font-display uppercase font-bold text-xl tracking-wide text-white group-hover:text-[#E53E3E] transition-colors mt-3 leading-tight">
                                    {p.title}
                                </h3>
                                <p className="text-gray-500 text-sm leading-relaxed mt-3">{p.excerpt}</p>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            <CtaBand />
        </div>
    );
}
